import { NgModule } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { ActionReducerMap, StoreModule } from '@ngrx/store';

import { AuthState } from '@ninety/mobile/platform/auth';

import { DatadogEffects } from './analytics/datadog/datadog.effects';
import { FullStoryEffects } from './analytics/fullstory/fullstory.effects';
import { GainsightEffects } from './analytics/gainsight/gainsight.effects';
import { NotificationSettingsIdentifyEffects } from './analytics/notifications/notification-settings-identify.effects';
import { SegmentEffects } from './analytics/segment/segment.effects';
import { AnalyticsSessionState } from './analytics/session-cross-link/analytics-session.model';
import { analyticsSessionReducer } from './analytics/session-cross-link/analytics-session.reducer';
import { SessionCrossLinkEffects } from './analytics/session-cross-link/session-cross-link.effects';
import { AppAnalyticsEffects } from './app-analytics.effects';
import {
  AnalyticsSessionStateKey,
  AppGlobalStateKey,
  CurrentPersonStateKey,
  LanguageStateKey,
  NotificationUiStateKey,
  SessionStateKey,
  ThemeStateKey,
  ViewportStateKey,
} from './app-global-keys';
import { LoggingEffects } from './app-logging/app-logging.effects';
import { CurrentPersonEffects } from './current-person/current-person-state.effects';
import { CurrentPersonStateModel } from './current-person/current-person-state.model';
import { currentPersonStateReducer } from './current-person/current-person-state.reducer';
import { IntercomStateEffects } from './intercom/intercom-state.effects';
import { LanguageEffects } from './language/language.effects';
import { LanguageStateModel, languageReducer } from './language/language.reducer';
import { NotificationUiState } from './notifications/notification-ui.model';
import { notificationUiReducer } from './notifications/notification-ui.reducer';
import { OneSignalEffects } from './one-signal/one-signal.effects';
import { NgrxRouterStoreModule } from './router/ngrx-router.module';
import { SessionAppEffects } from './session/session-app.effects';
import { SessionAuthorizationEffects } from './session/session-authorization.effects';
import { SessionBiometricsEffects } from './session/session-biometrics.effects';
import { SessionGrantEffects } from './session/session-grant.effects';
import { SessionLifecycleEffects } from './session/session-lifecycle.effects';
import { SessionTokensEffects } from './session/session-tokens.effects';
import { SessionVaultEffects } from './session/session-vault.effects';
import { SessionAnalyticsEffects } from './session/session.analytics.effects';
import { SessionEffects } from './session/session.effects';
import { sessionReducer } from './session/session.reducer';
import { SharingAnalyticsEffects } from './sharing/sharing-analytics.effects';
import { SignUpStateAnalyticsEffects } from './sign-up/sign-up-state.analytics.effects';
import { ThemeStateEffects } from './theme/theme.effects';
import { ThemeState } from './theme/theme.model';
import { themeStateReducer } from './theme/theme.reducer';
import { ViewportStateEffects } from './viewport/viewport.effects';
import { ViewportState } from './viewport/viewport.model';
import { viewportStateReducer } from './viewport/viewport.reducer';

export interface AppGlobalState {
  [SessionStateKey]: AuthState;
  [CurrentPersonStateKey]: CurrentPersonStateModel;
  [LanguageStateKey]: LanguageStateModel;
  [NotificationUiStateKey]: NotificationUiState;
  [ViewportStateKey]: ViewportState;
  [ThemeStateKey]: ThemeState;
  [AnalyticsSessionStateKey]: AnalyticsSessionState;
}

export const appStateReducers: ActionReducerMap<AppGlobalState> = {
  [SessionStateKey]: sessionReducer,
  [CurrentPersonStateKey]: currentPersonStateReducer,
  [LanguageStateKey]: languageReducer,
  [NotificationUiStateKey]: notificationUiReducer,
  [ViewportStateKey]: viewportStateReducer,
  [ThemeStateKey]: themeStateReducer,
  [AnalyticsSessionStateKey]: analyticsSessionReducer,
};

/**
 * ## The auth effects, and the order they are listed in
 *
 * `SessionEffects` is the machine — the one place `transition()` is called. Everything named
 * `Session*` after it is a Command handler, and between them they cover the Command union:
 * `SessionVaultEffects` the Vault, `SessionBiometricsEffects` the stored-credential replay,
 * `SessionGrantEffects` and `SessionTokensEffects` the two servers,
 * `SessionAuthorizationEffects` the browser leg of a federated Grant, `SessionAppEffects` the rest of
 * the app, `SessionLifecycleEffects` the launch and resume, and `SessionAnalyticsEffects` the one
 * product event auth reports to Segment.
 *
 * The list is complete: every Command in the union has a handler registered here, and every auth flow
 * goes through `transition()`.
 *
 * Registration order does not matter to correctness — `SessionEffects` guarantees the state has
 * advanced before any Command is handled, and `CommandRunner` fans them out with `mergeMap`. The
 * grouping is for reading.
 */
@NgModule({
  imports: [
    StoreModule.forFeature(AppGlobalStateKey, appStateReducers),
    EffectsModule.forFeature([
      SessionEffects,
      SessionVaultEffects,
      SessionBiometricsEffects,
      SessionGrantEffects,
      SessionAuthorizationEffects,
      SessionTokensEffects,
      SessionAppEffects,
      SessionLifecycleEffects,
      SessionAnalyticsEffects,
      CurrentPersonEffects,
      IntercomStateEffects,
      LanguageEffects,
      LoggingEffects,
      OneSignalEffects,
      SegmentEffects,
      GainsightEffects,
      DatadogEffects,
      FullStoryEffects,
      SessionCrossLinkEffects,
      ViewportStateEffects,
      ThemeStateEffects,
      SignUpStateAnalyticsEffects,
      SharingAnalyticsEffects,
      NotificationSettingsIdentifyEffects,
      AppAnalyticsEffects,
    ]),
    NgrxRouterStoreModule,
  ],
  // No providers. The auth machine's ports are `EnvironmentProviders` and are registered by the app
  // at bootstrap — `provideAuth()` in `main.ts`. They cannot live here: an environment initializer
  // reaching the injector through `importProvidersFrom` never takes effect, and nothing reports that
  // it did not run.
  providers: [],
})
export class AppGlobalStateModule {}
